/**
 * Authorization Types
 *
 * Types for frontend authorization and role-based navigation.
 */

/**
 * Per-project configuration returned from backend after login.
 * Controls which features are enabled and how the UI is labelled.
 */
export interface ProjectConfig {
  projectBaseUrl: string;
  identifierLabel: string;
  services: {
    downloadReport: boolean;
    addPatient: boolean;
    addAppointment: boolean;
    consultationAiBlocks: boolean;
    showStats: boolean;
    showDoctorUi: boolean;
    [key: string]: boolean;
  };
  medicineCatalogueEnabled?: boolean;
  /**
   * General-purpose hospital-level settings that aren't simple on/off flags.
   * e.g. otherConfig.prescriptionConfig.prescriptionSize.size = 'A5' controls
   * the Print Prescription page size for this hospital (default A4).
   */
  otherConfig?: {
    prescriptionConfig?: {
      prescriptionSize?: {
        size?: 'A4' | 'A5';
      };
    };
  };
  endpoints: Record<string, string>;
}

/**
 * Department key identifying the doctor's department.
 * Returned by backend in login and /auth/me responses.
 *
 * Deliberately not a union of known departments: the set is defined by the
 * specialty ValueSet in Medplum and the per-project department config, so
 * enumerating it here would mean a frontend release every time a hospital adds
 * a department — and any key not in the list got silently discarded.
 */
export type DepartmentKey = string | null;

/**
 * Field options configuration for case-sheet fields.
 * If a field name exists here with enabled: true, it renders as a dropdown
 * with the provided options. Otherwise, it renders as a text input.
 */
export interface FieldOptions {
  [fieldName: string]: {
    enabled: boolean;
    options: string[];
  };
}

/**
 * Department-specific case-sheet UI configuration.
 * Controls which fields to render, in what order, and visibility rules.
 * Returned by backend in login and /auth/me responses.
 */
export interface DepartmentConfig {
  /** Template identifier (e.g., "cardiology-default") */
  templateKey: string | null;
  /**
   * Ordered list of UI section names to render.
   * These can be either direct field names or grouped section names.
   * Render them in this exact order.
   */
  fieldOrder: string[];
  /**
   * Maps UI section names to arrays of database field names.
   * If a section name exists in fieldGroups, render it as a grouped section
   * combining data from all the listed fields.
   * If not present, treat the section name as a direct field name.
   *
   * Example:
   * {
   *   "otherHistory": ["medicalHistory", "familyHistory"],
   *   "examination": ["generalExamination", "treatmentPlan"]
   * }
   */
  fieldGroups?: Record<string, string[]>;
  /** Field options configuration for fields that need predefined options */
  fieldOptions: FieldOptions;
}

/**
 * Menu item from Medplum UserConfiguration
 * Backend only sends title and link - icons are handled by frontend
 */
export interface MenuItem {
  /** Display title for the menu item */
  title: string;
  /** Route path (e.g., "/dashboard", "/records") */
  link: string;
  /** Optional target (e.g., "_blank" for external links) */
  target?: string;
}

/**
 * User option from Medplum UserConfiguration
 */
export interface UserOption {
  id: string;
  valueBoolean?: boolean;
  valueString?: string;
  valueCode?: string;
}

/**
 * Medplum UserConfiguration resource
 * Controls UI visibility and user preferences
 */
export interface UserConfiguration {
  resourceType: 'UserConfiguration';
  id?: string;
  name?: string;
  /** Menu items to display in navigation */
  menu?: MenuItem[];
  /** Saved searches */
  search?: Array<{
    name: string;
    criteria: string;
  }>;
  /** User-specific options/feature flags */
  option?: UserOption[];
}

/**
 * Canonical role codes. Use these constants instead of raw strings everywhere.
 */
export const UserRoles = {
  ADMIN: 'admin',
  FRONTDESK: 'frontdesk',
  PRACTITIONER: 'practitioner',
} as const;

/**
 * What a role may DO, beyond which pages it can open. The codes come from the
 * user's AccessPolicy in Medplum (sent by the backend at login and on /auth/me);
 * the backend enforces them, the UI only uses them to hide what isn't allowed.
 */
export const CAPABILITIES = {
  /** Start a consultation and open its consultation / case sheet screens. */
  CONSULTATION_START: 'consultation.start',
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

/**
 * User roles supported by the system
 */
export type UserRole = (typeof UserRoles)[keyof typeof UserRoles];
/**
 * Role configuration mapping
 * Maps role codes to display label and default redirect path.
 */
export const ROLE_CONFIG: Record<UserRole, { label: string; defaultRedirect: string }> = {
  [UserRoles.ADMIN]: {
    label: 'Administrator',
    defaultRedirect: '/dashboard',
  },
  [UserRoles.PRACTITIONER]: {
    label: 'Practitioner',
    defaultRedirect: '/dashboard',
  },
  [UserRoles.FRONTDESK]: {
    label: 'Front Desk',
    defaultRedirect: '/appointments',
  },
};
